import { cn } from '@/shared/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Spinner } from './Spinner';

/**
 * Figma Button.
 * - variant: primary(검정) · secondary(회색) · ghost(배경 없음) · accent(파랑, 모달 확인) · outline(흰 배경 + 테두리, 모달 취소)
 * - size: lg(하단 CTA 18/SB) · md(팝업 48px 16/SB) · sm(로그아웃 같은 12/SB) · icon-lg(48) · icon-sm(32)
 */
const buttonVariants = cva(
  'inline-flex shrink-0 cursor-pointer items-center justify-center overflow-clip transition-colors disabled:cursor-default disabled:opacity-40',
  {
    variants: {
      variant: {
        primary:
          'bg-grey-900 text-fg-white hover:bg-grey-900/90 disabled:hover:bg-grey-900',
        secondary:
          'bg-grey-100 text-fg-secondary hover:bg-line-grey disabled:hover:bg-grey-100',
        ghost: 'hover:bg-grey-100 disabled:hover:bg-transparent',
        accent:
          'bg-primary-500 text-fg-white hover:bg-primary-600 disabled:hover:bg-primary-500',
        outline:
          'border-[1.5px] border-line-grey bg-surface-white text-fg-primary hover:bg-surface-grey disabled:hover:bg-surface-white',
      },
      size: {
        lg: 'rounded-20 px-5 py-4 text-button-18 font-semibold',
        md: 'h-12 rounded-16 px-4 text-button-16 font-semibold',
        sm: 'h-8 gap-0 rounded-12 p-2 text-button-12 font-semibold',
        'icon-lg': 'size-12 rounded-16',
        'icon-sm': 'size-8 rounded-12',
      },
    },
    defaultVariants: { variant: 'primary', size: 'lg' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** 처리 중 — 글자 앞에 스피너를 보이고 누를 수 없게 한다 */
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      type = 'button',
      isLoading = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      className={cn(
        buttonVariants({ variant, size }),
        isLoading && 'gap-2 disabled:opacity-100',
        className,
      )}
      {...props}
    >
      {isLoading && (
        <Spinner
          size="sm"
          className={
            variant === 'primary' || variant === 'accent' || !variant
              ? 'text-fg-white'
              : undefined
          }
        />
      )}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';
